import React             from 'react';
import Table             from '@widesk-ui/components/table/Table';
import { TableProps }    from '@widesk-ui/components/table/Table';
import { ModelClass }    from '@mathquis/modelx/lib/types/collection';
import ApiModel          from '@widesk-core/models/ApiModel';
import PagedCollection   from '@widesk-core/models/PagedCollection';
import { InitialFields } from '@widesk-ui/components/FilterBar';
import Input             from '@widesk-ui/components/form/Input';
import useCollection     from '@widesk-core/hooks/useCollection';
import ApiCollection     from '@widesk-core/models/ApiCollection';
import { Column }        from '@widesk-ui/components/table/Table';
import { message }       from '@widesk-ui/hooks/useMessage';
import _omit             from 'lodash/omit';

export type TableCollectionProps<M extends ModelClass<ApiModel>> =
	Partial<Omit<TableProps<InstanceType<M>[], any>, 'columns'>>
	& {
	modelClass: M;
	collectionFilters?: Record<string, CollectionFilter<M>>;
	columns: Column<InstanceType<M>>[];
	onFetchCollectionStart?: (collection: PagedCollection<InstanceType<M>>) => Promise<unknown | boolean>;
	onFetchCollectionEnd?: (collection: PagedCollection<InstanceType<M>>) => Promise<unknown | boolean>;
	defaultSortName: ModelSortName<InstanceType<M>>;
	defaultSortWay?: 'asc' | 'desc';
	positionEditable?: boolean;
	searchDisabled?: boolean;
	translations?: LocaleHeaderValue;
	includeFiles?: boolean;
};

const TableCollection = <M extends ModelClass<ApiModel>>(props: TableCollectionProps<M>) => {
	const collectionClass = props.pagination === false ? ApiCollection : PagedCollection;
	const collection = useCollection(() => {
		const coll = new collectionClass(props.modelClass);
		if (coll instanceof PagedCollection && props.pagination) {
			coll.setItemsPerPage(props.pagination.pageSize || 10);
		} 

		return coll as ApiCollection<ApiModel>;
	}) as PagedCollection<InstanceType<M>> | ApiCollection<InstanceType<M>>;

	const collectionFilters = props.collectionFilters || {};

	const actions = React.useMemo<TableCollectionProps<M>['actions']>(() => props.actions, [props.actions]);

	const columns = props.columns.map(col => {
		if (col.sortBy === props.defaultSortName) {
			col.sortOrder = props.defaultSortWay === 'desc' ? 'descend' : 'ascend';
		}
		return col;
	});

	return (
		<Table
			rowKey={m => m.id?.toString() || ''}
			fetch={async (page, step, filters) => {
				if (collection instanceof PagedCollection) {
					if (page) {
						collection.setPage(page);
					}

					if (step) {
						collection.setItemsPerPage(step);
					}
				}

				if (filters) {
					const filtersFromCollectionFilters = await getFiltersFromCollectionFilters(collectionFilters, filters);
					collection.setFilters(filtersFromCollectionFilters as ModelFilters<InstanceType<M>>);
				}

				if (props.onFetchCollectionStart) {
					if (await props.onFetchCollectionStart(collection as never) === false) {
						return []; // Si la méthode retourne false, le fetch n'est pas exécuté
					}
				}

				await collection
					.setIncludeFiles(props.includeFiles)
					.setLocale(props.translations)
					.list();

				if (props.onFetchCollectionEnd) {
					await props.onFetchCollectionEnd(collection as never);
				}

				// Le tableau doit être clôné afin qu'Antd puisse correctement déclencher le re-render
				return [...collection.models] as any[];
			}}

			defaultFilters={{
				[`order[${props.defaultSortName}]`]: props.defaultSortWay || 'asc',
				...props.defaultFilters,
			}}

			filters={Object.entries({
				...(props.searchDisabled ? {} : {
					search: {
						element: <Input placeholder="Recherche" />,
						filter: 'search',
						label: 'Recherche par mot clé',
						forceDisplay: true,
						displayRight: true,
					},
				}),

				...collectionFilters,
			}).reduce<InitialFields>((acc, [name, filter]) => {
				acc[name] = { ...filter as any };
				return acc;
			}, {}) as Record<any, any>}

			// storageKey={'table_' + props.modelClass.path}

			{...props as any}

			onDidMount={tableStore => {
				tableStore.collection = collection;
				props.onDidMount?.(tableStore);
			}}

			actions={actions}

			columns={columns.map(column => ({
				...column,
				sortBy: column.sortBy ? `order[${column.sortBy}]` : undefined,
			}))}

			pagination={props.pagination === false ? false : { pagedCollection: collection, ...props.pagination }}

			onChangePosition={async (data) => {
				const model = collection.models
					.find(m => (m as any).position === data.oldPosition);

				try {
					await model?.patch({ position: data.position });

					message.success('Position modifiée avec succès');

					await data.store.reload();
				} catch (err) {
					await data.store.reload();
				}
			}}

			sortable={props.positionEditable}
		/>
	);
};

/**
 * Permet de gérer la propriété "filter" lorsqu'il s'agit d'une méthode asynchrone ou d'un string
 */
const getFiltersFromCollectionFilters = async (
	collectionFilters: Record<string, CollectionFilter<any>>,
	filters: Record<string, unknown>,
) => {
	const result: Record<string, unknown> = {};

	await Promise.all(
		Object.entries(filters).map(
			async ([key, value]) => {
				const collectionFilter = collectionFilters[key];

				if (typeof value === 'undefined' || key === 'sortWay') {
					return;
				}

				if (key === 'sortName' && typeof value === 'string') {
					result[`order[${value}]`] = filters['sortWay'] || 'asc';
				} else if (collectionFilter) { // Le filtre est défini dans la liste des filtres de la SplitView
					if (Array.isArray(collectionFilter.filter)) {
						if (!Array.isArray(value)) {
							throw `La "value" pour le filtre "${key}" doit être un tableau`;
						}

						collectionFilter.filter.forEach((fName, idx) => result[fName] = value[idx]);

					} else if (typeof collectionFilter.filter === 'function') { // "filter" est une méthode
						const filterRes = await collectionFilter.filter(value);
						Object.assign(result, filterRes);
					} else { // "filter" est un string
						result[collectionFilter.filter] = value;
					}
				} else {
					result[key] = value;
				}
			},
		),
	);

	return _omit(result, ['idx']);
};

export default TableCollection;
